import { useState, useEffect, useCallback, useRef } from 'react';
import PageTransition from '../components/PageTransition';
import { useNavigate, useSearchParams, useNavigationType } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Category, Model, PartGroup, SparePart, CartItem } from '../types/SpareParts';
import { getCategories, getModels, getPartGroups, getSpareParts, getSparePartsByCategory, getSparePartsByModel, getPromotionalSpareParts, getCartItems, searchSpareParts, addToCart } from '../services/sparePartsService';
import Toast, { ToastType } from '../components/Toast';
import CategorySelector from '../components/CategorySelector';
import ModelSelector from '../components/ModelSelector';
import SparePartsGroupSelector from '../components/SparePartsGroupSelector';
import SparePartCard from '../components/SparePartCard';
import PartSearchModal from '../components/PartSearchModal';

import { PAGE_SIZES } from '../constants/pagination';
import { useMenu } from '../contexts/MenuContext';

// Yardımcı: ağaç yapısında self ile öğe bul
function findBySelf<T extends { self: number; children: T[] | null }>(items: T[], self: string): T | null {
  for (const item of items) {
    if (item.self.toString() === self) return item;
    if (item.children) {
      const found = findBySelf(item.children as T[], self);
      if (found) return found;
    }
  }
  return null;
}

export default function SpareParts() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const navigationType = useNavigationType();
  const [loading, setLoading] = useState(false);
  const { isMenuButtonVisible, isSidebarOpen, isMobile } = useMenu();
  const queryClient = useQueryClient();
  
  // Scroll yönünü takip etmek için state ve ref
  const [showFixedButtons, setShowFixedButtons] = useState(true);
  const [showSearchButton, setShowSearchButton] = useState(true);
  const [showBackButton, setShowBackButton] = useState(true);
  const lastScrollTop = useRef(0);
  
  // Scroll restoration için
  const scrollSaveTimeout = useRef<NodeJS.Timeout | null>(null);
  const isRestoringScroll = useRef(false);
  
  // Kategori seçimi için state değişkenleri
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  
  // Model seçimi için state değişkenleri
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  
  // Parça grubu seçimi için state değişkenleri
  const [selectedPartGroup, setSelectedPartGroup] = useState<PartGroup | null>(null);
  
  // Yedek parça listesi için state değişkenleri
  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam) : 1;
  });
  const [hideOutOfStock, setHideOutOfStock] = useState(() => searchParams.get('hideOutOfStock') === 'true');
  
  // İlk yükleme durumunu kontrol eden state
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Arama ile ilgili state değişkenleri
  const [isSearchMode, setIsSearchMode] = useState(() => searchParams.get('searchMode') === 'true');
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('searchQuery') || '');
  const [searchType, setSearchType] = useState<'code' | 'description'>(() => {
    const type = searchParams.get('searchType');
    return type === 'code' || type === 'description' ? type : 'description';
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  const [isActiveSearch, setIsActiveSearch] = useState(false); // Aktif arama yapıldığını takip eder

  // URL'den gelen seçimleri geçici olarak tutar
  const [pendingCategoryGuid, setPendingCategoryGuid] = useState<string | null>(() => searchParams.get('category'));
  const [pendingModelGuid, setPendingModelGuid] = useState<string | null>(() => searchParams.get('model'));
  const [pendingPartGroupGuid, setPendingPartGroupGuid] = useState<string | null>(() => searchParams.get('partGroup'));
  const [isUrlRestoring, setIsUrlRestoring] = useState<boolean>(() => {
    // URL'de herhangi bir seçim parametresi varsa restore modunda başla
    return !!(searchParams.get('category') || searchParams.get('model') || searchParams.get('partGroup'));
  });
  
  // Arama öncesi seçimleri kaydetmek için state değişkenleri
  const [preSearchCategory, setPreSearchCategory] = useState<Category | null>(null);
  const [preSearchModel, setPreSearchModel] = useState<Model | null>(null);
  const [preSearchPartGroup, setPreSearchPartGroup] = useState<PartGroup | null>(null);
  
  // Sepet ile ilgili state değişkenleri - React Query ile yönetilecek
  
  // Modal state değişkenleri
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isModelModalOpen, setIsModelModalOpen] = useState(false);
  const [isPartGroupModalOpen, setIsPartGroupModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [modelSearchTerm, setModelSearchTerm] = useState('');
  const [partGroupSearchTerm, setPartGroupSearchTerm] = useState('');
  
  // Toast ile ilgili state değişkenleri
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'info' as ToastType
  });
  
  // Toast gösterme fonksiyonu
  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };
  
  // Toast kapatma fonksiyonu
  const hideToast = () => {
    setToast(prev => ({
      ...prev,
      isVisible: false
    }));
  };

  // URL parametrelerini güncelleyen yardımcı fonksiyon
  const updateUrlParams = (params: {
    page?: number;
    searchMode?: boolean;
    searchQuery?: string;
    searchType?: 'code' | 'description';
    hideOutOfStock?: boolean;
    category?: string | null;
    model?: string | null;
    partGroup?: string | null;
    replaceHistory?: boolean; // Yeni parametre: geçmişi değiştir mi yoksa yeni giriş ekle mi
  }) => {
    const newParams = new URLSearchParams(window.location.search);

    // Helper to set/delete params
    const handleParam = (key: string, value: string | number | boolean | null | undefined) => {
      if (value === null || value === undefined || value === '' || value === false) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    };

    if (params.page !== undefined) {
      // Sayfa parametresi 1'den büyükse URL'e ekle
      handleParam('page', params.page > 1 ? params.page : null);
      }
    if (params.hideOutOfStock !== undefined) {
      handleParam('hideOutOfStock', params.hideOutOfStock ? 'true' : null);
      }

    if (params.searchMode !== undefined) {
      handleParam('searchMode', params.searchMode);
      if (params.searchMode) {
        handleParam('searchQuery', params.searchQuery);
        handleParam('searchType', params.searchType);
        // Arama moduna girildiğinde kategori/model/grup filtrelerini temizle
        newParams.delete('category');
        newParams.delete('model');
        newParams.delete('partGroup');
      } else {
        // searchMode false ise, ilgili parametreleri temizle
        newParams.delete('searchQuery');
        newParams.delete('searchType');
    }
    }

    // Kategori, model ve parça grubu parametrelerini güncelle
    if (params.category !== undefined) {
      handleParam('category', params.category);
    }
    if (params.model !== undefined) {
      handleParam('model', params.model);
    }
    if (params.partGroup !== undefined) {
      handleParam('partGroup', params.partGroup);
    }

    // History yönetimi için navigation türünü belirle
    // Default: replace: false (yeni history entry oluştur)
    // Sadece açıkça belirtildiğinde replace: true (mevcut entry'yi güncelle)
    const shouldReplace = params.replaceHistory === true;

    navigate({ search: newParams.toString() }, { replace: shouldReplace });
  };

  // React Query ile kategorileri çek
  const {
    data: categoriesData,
    isLoading: loadingCategories,
    error: categoryError
  } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const data = await getCategories();
      return data.children || [];
    },
    staleTime: 10 * 60 * 1000, // 10 dakika fresh tut (kategoriler sık değişmez)
    gcTime: 30 * 60 * 1000, // 30 dakika cache'te tut
  });

  const categories = categoriesData || [];

  // React Query ile sepet içeriğini çek
  const {
    data: cartData,
    isLoading: loadingCart,
    error: cartError
  } = useQuery({
    queryKey: ['cart'],
    queryFn: async () => {
      const data = await getCartItems();
      return data;
    },
    staleTime: 1 * 60 * 1000, // 1 dakika fresh tut (sepet sık değişebilir)
    gcTime: 5 * 60 * 1000, // 5 dakika cache'te tut
  });

  const cartItems = cartData?.data || [];
  const cartItemCount = cartData?.total || 0;

  // React Query ile modelleri çek - sadece kategori seçiliyse
  const {
    data: modelsData,
    isLoading: loadingModels,
    error: modelError
  } = useQuery({
    queryKey: ['models', selectedCategory?.self],
    queryFn: async () => {
      if (!selectedCategory) return { children: [] };
      const data = await getModels(selectedCategory);
      return data;
    },
    enabled: isComponentMounted && !!selectedCategory,
    staleTime: 10 * 60 * 1000, // 10 dakika fresh tut (modeller sık değişmez)
    gcTime: 30 * 60 * 1000, // 30 dakika cache'te tut
  });

  const models = modelsData?.children || [];

  // React Query ile parça gruplarını çek - sadece kategori ve model seçiliyse
  const {
    data: partGroupsData,
    isLoading: loadingPartGroups,
    error: partGroupError
  } = useQuery({
    queryKey: ['partGroups', selectedCategory?.self, selectedModel?.self],
    queryFn: async () => {
      if (!selectedCategory || !selectedModel) return { children: [] };
      const data = await getPartGroups(selectedCategory, selectedModel);
      return data;
    },
    enabled: isComponentMounted && !!selectedCategory && !!selectedModel,
    staleTime: 10 * 60 * 1000, // 10 dakika fresh tut (parça grupları sık değişmez)
    gcTime: 30 * 60 * 1000, // 30 dakika cache'te tut
  });

  const partGroups = partGroupsData?.children || [];

  // React Query ile yedek parçaları çek - farklı durumlar için
  const sparePartsQueryKey = isSearchMode 
    ? ['spareParts', 'search', searchQuery, searchType, currentPage, hideOutOfStock]
    : selectedPartGroup && selectedModel
    ? ['spareParts', 'partGroup', selectedModel.self, selectedPartGroup.self, currentPage, hideOutOfStock]
    : selectedModel
    ? ['spareParts', 'model', selectedModel.self, currentPage, hideOutOfStock]
    : selectedCategory
    ? ['spareParts', 'category', selectedCategory.self, currentPage, hideOutOfStock]
    : ['spareParts', 'promotional', currentPage, hideOutOfStock];

  const {
    data: sparePartsData,
    isLoading: loadingSpareParts,
    error: sparePartsError,
    isRefetching: refreshingSpareParts
  } = useQuery({
    queryKey: sparePartsQueryKey,
    queryFn: async () => {
      if (isSearchMode && searchQuery) {
        return await searchSpareParts(
          searchQuery,
          searchType,
          currentPage,
          PAGE_SIZES.SPARE_PARTS,
          hideOutOfStock,
          undefined,
          undefined,
          undefined
        );
      } else if (selectedPartGroup && selectedModel) {
        return await getSpareParts(selectedModel, selectedPartGroup, currentPage, PAGE_SIZES.SPARE_PARTS, hideOutOfStock);
      } else if (selectedModel) {
        return await getSparePartsByModel(selectedModel, currentPage, PAGE_SIZES.SPARE_PARTS, hideOutOfStock);
      } else if (selectedCategory) {
        return await getSparePartsByCategory(selectedCategory, currentPage, PAGE_SIZES.SPARE_PARTS, hideOutOfStock);
      } else {
        return await getPromotionalSpareParts(currentPage, PAGE_SIZES.SPARE_PARTS, hideOutOfStock);
      }
    },
    enabled: isComponentMounted && !isUrlRestoring, // Component mount edildikten sonra ve URL restore tamamlandıktan sonra çalışsın
    staleTime: 2 * 60 * 1000, // 2 dakika fresh tut
    gcTime: 5 * 60 * 1000, // 5 dakika cache'te tut
  });

  // Yedek parça verilerini çıkar
  const spareParts = sparePartsData?.data || [];
  const totalSpareParts = sparePartsData?.total || 0;
  const totalPages = Math.ceil(totalSpareParts / PAGE_SIZES.SPARE_PARTS);



  // Component mount edildikten sonra flag'i set et
  useEffect(() => {
    setIsComponentMounted(true);
  }, []);

  // Navigation türüne göre scroll yönetimi
  useEffect(() => {
    if (!isComponentMounted) return;

    const currentUrl = window.location.pathname + window.location.search;
    const scrollKey = `scroll-${currentUrl}`;

    // POP navigation (geri/ileri tuş) - scroll pozisyonunu restore et
    if (navigationType === 'POP') {
      const savedPosition = sessionStorage.getItem(scrollKey);
      if (savedPosition) {
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedPosition));
        }, 100); // DOM güncellemelerini bekle
      }
    }
    // PUSH/REPLACE navigation - scroll'u en üste al
    else if (navigationType === 'PUSH' || navigationType === 'REPLACE') {
      window.scrollTo(0, 0);
    }
  }, [navigationType, isComponentMounted]);

  // Sayfa ayrılırken scroll pozisyonunu kaydet
  useEffect(() => {
    const saveScrollPosition = () => {
      const currentUrl = window.location.pathname + window.location.search;
      const scrollKey = `scroll-${currentUrl}`;
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      sessionStorage.setItem(scrollKey, scrollPosition.toString());
    };

    // Scroll pozisyonunu periyodik olarak kaydet
    const handleScroll = () => {
      if (scrollSaveTimeout.current) {
        clearTimeout(scrollSaveTimeout.current);
      }
      scrollSaveTimeout.current = setTimeout(saveScrollPosition, 100);
    };

    // Sayfa ayrılırken kaydet
    const handleBeforeUnload = saveScrollPosition;

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      saveScrollPosition(); // Component unmount olurken de kaydet
    };
  }, []);



  // URL parametreleri değiştiğinde state'i senkronize et
  useEffect(() => {
    if (!isComponentMounted) return;

    const searchMode = searchParams.get('searchMode') === 'true';
    const query = searchParams.get('searchQuery') || '';
    const type = (searchParams.get('searchType') as 'code' | 'description') || 'description';
    const page = searchParams.get('page');
    const hideStock = searchParams.get('hideOutOfStock') === 'true';
    const categoryParam = searchParams.get('category');
    const modelParam = searchParams.get('model');
    const partGroupParam = searchParams.get('partGroup');

    // Arama parametrelerini güncelle
    setIsSearchMode(searchMode);
    setSearchQuery(query);
    setSearchType(type);

    // Arama modundan çıkıldığında önceki seçimleri geri yükle
    if (!searchMode && isSearchMode) {
      // Arama modundan normal moda geçiş - önceki seçimleri temizle
      setTimeout(() => {
        setPreSearchCategory(null);
        setPreSearchModel(null);
        setPreSearchPartGroup(null);
      }, 100); // Kısa gecikme ile temizle
    }

    // Sayfa parametresini güncelle
    const pageNumber = page ? parseInt(page) : 1;
    if (pageNumber !== currentPage && pageNumber > 0 && !isNaN(pageNumber)) {
      setCurrentPage(pageNumber);
    }

    // Stok filtresi parametresini güncelle
    if (hideStock !== hideOutOfStock) {
      setHideOutOfStock(hideStock);
    }

    // Kategori parametresini güncelle
    if (categoryParam && categories.length > 0) {
      const cat = findBySelf(categories, categoryParam);
      if (cat && (!selectedCategory || cat.self !== selectedCategory.self)) {
        setSelectedCategory(cat);
        // Model ve parça grubu seçimini sıfırla (kategori değişti)
        setSelectedModel(null);
        setSelectedPartGroup(null);
      }
    } else if (!categoryParam && selectedCategory) {
      // URL'de kategori yok ama state'te var, temizle
      setSelectedCategory(null);
      setSelectedModel(null);
      setSelectedPartGroup(null);
    }

    // Model parametresini güncelle (sadece kategori seçili ve modeller yüklü ise)
    if (modelParam && selectedCategory && models.length > 0) {
      const mdl = findBySelf(models, modelParam);
      if (mdl && (!selectedModel || mdl.self !== selectedModel.self)) {
        setSelectedModel(mdl);
        // Parça grubu seçimini sıfırla (model değişti)
        setSelectedPartGroup(null);
      }
    } else if (!modelParam && selectedModel) {
      // URL'de model yok ama state'te var, temizle
      setSelectedModel(null);
      setSelectedPartGroup(null);
    }

    // Parça grubu parametresini güncelle (sadece model seçili ve parça grupları yüklü ise)
    if (partGroupParam && selectedModel && partGroups.length > 0) {
      const grp = findBySelf(partGroups, partGroupParam);
      if (grp && (!selectedPartGroup || grp.self !== selectedPartGroup.self)) {
        setSelectedPartGroup(grp);
      }
    } else if (!partGroupParam && selectedPartGroup) {
      // URL'de parça grubu yok ama state'te var, temizle
      setSelectedPartGroup(null);
    }

  }, [searchParams, isComponentMounted, categories, models, partGroups, hideOutOfStock, selectedCategory, selectedModel, selectedPartGroup, isSearchMode]);

  // React Query otomatik olarak kategorileri ve sepeti yükler

  // Scroll olayını dinle - sabit butonları gizle/göster
  useEffect(() => {
    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      // Modal açıksa butonları gizle
      if (isModalOpen) {
        setShowFixedButtons(false);
        setShowSearchButton(false);
        setShowBackButton(false);
        return;
      }
      
      // Modal kapandıysa, mevcut scroll pozisyonuna göre butonların durumunu yeniden değerlendir
      // Scroll pozisyonu ayarlı değilse default olarak göster
      if (lastScrollTop.current === undefined || lastScrollTop.current === null) {
        setShowFixedButtons(true);
        setShowSearchButton(true);
        setShowBackButton(true);
        lastScrollTop.current = st;
        return;
      }
      
      // Tüm butonlar için aynı threshold (10px) - aynı anda belirip kaybolsun
      const threshold = 10;
      if (st > lastScrollTop.current + threshold) {
        setShowFixedButtons(false);
        setShowSearchButton(false);
        setShowBackButton(false);
      } else if (st < lastScrollTop.current - threshold) {
        setShowFixedButtons(true);
        setShowSearchButton(true);
        setShowBackButton(true);
      }
      
      // Son scroll pozisyonunu kaydet
      lastScrollTop.current = st <= 0 ? 0 : st;
    };

    // Modal kapanma durumunu özel olarak kontrol eden fonksiyon
    const handleModalStateChange = () => {
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      if (!isModalOpen) {
        // Modal kapandı - butonları tekrar göster ve scroll pozisyonunu sıfırla
        const st = window.pageYOffset || document.documentElement.scrollTop;
        
        // Butonları anında göster (çünkü modal kapandı)
        setShowFixedButtons(true);
        setShowSearchButton(true);
        setShowBackButton(true);
        
        // Scroll pozisyonunu güncelle
        lastScrollTop.current = st;
        
        // Kısa bir delay ile normal scroll davranışını başlat
        setTimeout(() => {
          handleScroll();
        }, 50);
      } else {
        // Modal açıldı - butonları gizle
        setShowFixedButtons(false);
        setShowSearchButton(false);
        setShowBackButton(false);
      }
    };

    // Scroll listener'ı ekle
    window.addEventListener('scroll', handleScroll);
    
    // Body attribute değişikliklerini izle (modal açılıp kapanması için)
    const observer = new MutationObserver(handleModalStateChange);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-modal-open']
    });
    
    // İlk kontrol
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  // URL'den gelen kategori seçimini uygula
  useEffect(() => {
    if (pendingCategoryGuid && categories.length > 0 && !selectedCategory) {
      const cat = findBySelf(categories, pendingCategoryGuid);
      if (cat) {
        setSelectedCategory(cat);
        setPendingCategoryGuid(null);
        // React Query otomatik olarak modelleri yükleyecek
      }
    }
  }, [pendingCategoryGuid, categories, selectedCategory]);

  // URL'den gelen model seçimini uygula
  useEffect(() => {
    if (pendingModelGuid && models.length > 0 && selectedCategory && !selectedModel) {
      const mdl = findBySelf(models, pendingModelGuid);
      if (mdl) {
        setSelectedModel(mdl);
        setPendingModelGuid(null);
        // React Query otomatik olarak parça gruplarını yükleyecek
      }
    }
  }, [pendingModelGuid, models, selectedCategory, selectedModel]);

  // URL'den gelen parça grubu seçimini uygula
  useEffect(() => {
    if (pendingPartGroupGuid && partGroups.length > 0 && selectedModel && !selectedPartGroup) {
      const grp = findBySelf(partGroups, pendingPartGroupGuid);
      if (grp) {
        setSelectedPartGroup(grp);
        setPendingPartGroupGuid(null);
      }
    }
  }, [pendingPartGroupGuid, partGroups, selectedModel, selectedPartGroup]);

  // URL restore işleminin bitip bitmediğini kontrol et
  useEffect(() => {
    if (isUrlRestoring) {
      // Tüm pending parametreler temizlendiyse restore tamamlandı
      if (!pendingCategoryGuid && !pendingModelGuid && !pendingPartGroupGuid) {
        setIsUrlRestoring(false);
      }
    }
  }, [isUrlRestoring, pendingCategoryGuid, pendingModelGuid, pendingPartGroupGuid]);

  // Arama sonuçları için toast göster
  useEffect(() => {
    if (isSearchMode && sparePartsData && isActiveSearch && currentPage === 1) {
      if (sparePartsData.total === 0) {
            showToast('Arama kriterlerinize uygun ürün bulunamadı.', 'warning');
          } else {
        showToast(`${sparePartsData.total} adet sonuç bulundu`, 'success');
          }
          setIsActiveSearch(false); // Toast gösterildikten sonra flag'i sıfırla
        }
  }, [sparePartsData, isActiveSearch, currentPage, isSearchMode]);

  // Hata durumunda toast göster
  useEffect(() => {
    if (categoryError) {
      console.error('Kategoriler yüklenirken hata:', categoryError);
      showToast('Kategoriler yüklenirken bir hata oluştu.', 'error');
    }
  }, [categoryError]);

  useEffect(() => {
    if (sparePartsError) {
      console.error('Yedek parçalar yüklenirken hata:', sparePartsError);
      showToast('Yedek parçalar yüklenirken bir hata oluştu.', 'error');
    }
  }, [sparePartsError]);

  useEffect(() => {
    if (modelError) {
      console.error('Modeller yüklenirken hata:', modelError);
      showToast('Modeller yüklenirken bir hata oluştu.', 'error');
    }
  }, [modelError]);

  useEffect(() => {
    if (partGroupError) {
      console.error('Parça grupları yüklenirken hata:', partGroupError);
      showToast('Parça grupları yüklenirken bir hata oluştu.', 'error');
    }
  }, [partGroupError]);





  // Eski fetch fonksiyonları React Query ile değiştirildi

  // İlk yükleme tamamlandığında flag'i güncelle
  useEffect(() => {
    if (!loadingCategories && !loadingSpareParts && isComponentMounted) {
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 300); // 300ms gecikme - daha hızlı buton gösterimi
      
      return () => clearTimeout(timer);
    }
  }, [loadingCategories, loadingSpareParts, isComponentMounted]);

  // Kategori seçimi
  const handleCategorySelect = (category: Category) => {
    // Modal'ı kapat
    setIsCategoryModalOpen(false);
    setCategorySearchTerm('');
    
    // URL restore modundan çık (manual seçim yapıldı)
    setIsUrlRestoring(false);
    
    // URL parametrelerini güncelle - useEffect otomatik olarak state'leri güncelleyecek
    updateUrlParams({
      page: 1,
      searchMode: false,
      hideOutOfStock,
      category: category.self.toString(),
      model: null,
      partGroup: null,
    });   
  };

  // Arama terimini güncelle
  const handleCategorySearchChange = (value: string) => {
    setCategorySearchTerm(value);
  };

  const handleModelSearchChange = (value: string) => {
    setModelSearchTerm(value);
  };

  const handlePartGroupSearchChange = (value: string) => {
    setPartGroupSearchTerm(value);
  };

  // Arama fonksiyonu
  const handleSearch = async (query: string, searchType: 'code' | 'description') => {
    try {
      setIsActiveSearch(true); // Aktif arama yapıldığını işaretle
      
      // Arama öncesi mevcut seçimleri kaydet
      setPreSearchCategory(selectedCategory);
      setPreSearchModel(selectedModel);
      setPreSearchPartGroup(selectedPartGroup);

      // URL güncelle - useEffect otomatik olarak state'leri güncelleyecek
      updateUrlParams({
        searchMode: true,
        searchQuery: query,
        searchType: searchType,
        page: 1,
      });
    } catch (error) {
      console.error('Arama hatası:', error);
      showToast('Arama sırasında bir hata oluştu.', 'error');
    }
  };

  // Model seçimi
  const handleModelSelect = (model: Model) => {
    // Modal'ı kapat
    setIsModelModalOpen(false);
    setModelSearchTerm('');
    
    // URL restore modundan çık (manual seçim yapıldı)
    setIsUrlRestoring(false);

    // URL parametrelerini güncelle - useEffect otomatik olarak state'leri güncelleyecek
    updateUrlParams({
      page: 1,
      searchMode: false,
      hideOutOfStock,
      category: selectedCategory?.self.toString(),
      model: model.self.toString(),
      partGroup: null,
    });
  };

  // Parça grubu seçimi
  const handlePartGroupSelect = (partGroup: PartGroup) => {
    // Modal'ı kapat
    setIsPartGroupModalOpen(false);
    setPartGroupSearchTerm('');
    
    // URL restore modundan çık (manual seçim yapıldı)
    setIsUrlRestoring(false);

    // URL parametrelerini güncelle - useEffect otomatik olarak state'leri güncelleyecek
    updateUrlParams({
      page: 1,
      searchMode: false,
      hideOutOfStock,
      category: selectedCategory?.self.toString(),
      model: selectedModel?.self.toString(),
      partGroup: partGroup.self.toString(),
    });
  };



    // Sayfa değiştir
  const handlePageChange = (page: number) => {
    // Sadece URL'yi güncelle - useEffect otomatik olarak state'i güncelleyecek
    // Scroll pozisyonu React Router'ın ScrollRestoration bileşeni tarafından yönetiliyor
    updateUrlParams({ page });
  };

  // Sepete ekle
  const handleAddToCart = async (sparePart: SparePart, quantity: number) => {
    try {
      // API'ye sepete ekleme isteği gönder - miktar bilgisini de gönder
      await addToCart(sparePart.Id, quantity);
      
      // React Query cache'ini invalidate et - sepet verilerini yenile
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      
      // Başarı mesajı göster
      showToast(`${sparePart.Ad} (${quantity} adet) sepete eklendi.`, 'success');
    } catch (error) {
      console.error('Sepete ekleme hatası:', error);
      showToast('Sepete ekleme sırasında bir hata oluştu.', 'error');
    }
  };

  // Geri gitme fonksiyonu
  const handleGoBack = () => {
    navigate(-1); // Bir önceki sayfaya git
  };

  // Stok filtresi toggle handler
  const handleHideOutOfStockToggle = () => {
    const newHideOutOfStock = !hideOutOfStock;
    
    // Sadece URL'yi güncelle - useEffect otomatik olarak state'leri güncelleyecek
    updateUrlParams({ hideOutOfStock: newHideOutOfStock });
  };

  // React Query otomatik olarak kategori, model, parça grubu değişikliklerini dinler



  // Ana sayfa içeriği
  const renderMainContent = () => {
    return (
      <div className="w-full">
        {/* Arama Durumu */}
        {isSearchMode && !loadingSpareParts && (
          <div className="mb-6 border border-blue-200 dark:border-blue-700 rounded-xl bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-blue-800/20 shadow-sm">
            <div className="rounded-xl p-5 border border-white dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 rounded-lg flex items-center justify-center mr-3 shadow-sm">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                    "{searchQuery}"
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    {searchType === 'code' ? 'Parça koduna göre' : 'Parça adına göre'} • <span className="font-medium">{totalSpareParts} sonuç</span>
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  // URL restore modundan çık
                  setIsUrlRestoring(false);
                  
                  // URL güncelle - useEffect otomatik olarak state'leri güncelleyecek
                  updateUrlParams({
                    searchMode: false,
                    page: 1,
                    category: preSearchCategory?.self.toString() || null,
                    model: preSearchModel?.self.toString() || null,
                    partGroup: preSearchPartGroup?.self.toString() || null,
                  });
                }}
                                 className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-800/50 border border-transparent hover:border-blue-200 dark:hover:border-blue-600 rounded-lg transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            </div>
          </div>
        )}

        {/* Header Section */}
        {!isSearchMode && (
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Yedek Parça Arama</h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Kategori, model ve parça grubu seçerek aradığınız yedek parçayı bulun</p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Kategori Seçimi */}
          {(!isSearchMode || loadingSpareParts || (isSearchMode && spareParts.length > 0)) && (
          <div className="w-full">
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Kategori
            </label>

            {categoryError ? (
              <div className="bg-yellow-50 dark:bg-yellow-900/50 border-l-4 border-yellow-500 p-4 rounded">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700 dark:text-yellow-200">
                      Kategoriler yüklenirken bir hata oluştu.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative">
                <div
                  onClick={loadingCategories ? undefined : () => setIsCategoryModalOpen(true)}
                  onKeyDown={loadingCategories ? undefined : (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsCategoryModalOpen(true);
                    }
                  }}
                  tabIndex={loadingCategories ? -1 : 0}
                  className={`form-combobox w-full relative bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 ${
                    loadingCategories ? 'cursor-not-allowed opacity-75' : 'cursor-pointer hover:border-gray-400 dark:hover:border-gray-500'
                  }`}
                >
                  {loadingCategories ? (
                    <>
                      <span className="text-gray-500 dark:text-gray-400 truncate pr-1">Yükleniyor...</span>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <svg className="animate-spin h-4 w-4 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    </>
                  ) : (
                    <>
                      <span 
                        className={`${selectedCategory ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'} truncate pr-1`}
                      >
                        {selectedCategory ? selectedCategory.name : 'Kategori seçin'}
                      </span>
                      <svg className={`h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isCategoryModalOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Model Seçimi */}
          {(!isSearchMode || loadingSpareParts || (isSearchMode && spareParts.length > 0)) && (
          <div className="w-full">
              <label htmlFor="model" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Model
              </label>

              {false ? (
                <div className="flex items-center justify-center py-8">
                  <svg className="animate-spin h-6 w-6 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : selectedCategory && modelError ? (
                <div className="bg-yellow-50 dark:bg-yellow-900/50 border-l-4 border-yellow-500 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700 dark:text-yellow-200">
                        {modelError?.message || 'Modeller yüklenirken bir hata oluştu.'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <div
                    onClick={selectedCategory && !loadingModels ? () => setIsModelModalOpen(true) : undefined}
                    tabIndex={selectedCategory && !loadingModels ? 0 : -1}
                    className={`form-combobox w-full relative ${
                      selectedCategory && !loadingModels
                        ? 'cursor-pointer bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 hover:border-gray-400 dark:hover:border-gray-500' 
                        : 'cursor-not-allowed opacity-50 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2'
                    } ${modelError ? 'border-red-300 dark:border-red-600' : ''}`}
                  >
                    {loadingModels ? (
                      <>
                        <span className="text-gray-500 dark:text-gray-400 truncate pr-1">Yükleniyor...</span>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <svg className="animate-spin h-4 w-4 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      </>
                    ) : (
                      <>
                        <span 
                          className={`${selectedModel ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'} truncate pr-1`}
                        >
                          {selectedModel ? selectedModel.name : selectedCategory ? 'Model seçin' : 'Önce kategori seçin'}
                        </span>
                        <svg className={`h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isModelModalOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </>
                    )}
                  </div>
                </div>
              )}
          </div>
          )}

          {/* Parça Grubu Seçimi */}
          {(!isSearchMode || loadingSpareParts || (isSearchMode && spareParts.length > 0)) && (
          <div className="w-full">
              <label htmlFor="partGroup" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Parça Grubu
              </label>

              {false ? (
                <div className="flex items-center justify-center py-8">
                  <svg className="animate-spin h-6 w-6 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : selectedCategory && selectedModel && partGroupError ? (
                <div className="bg-yellow-50 dark:bg-yellow-900/50 border-l-4 border-yellow-500 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700 dark:text-yellow-200">
                        {partGroupError?.message || 'Parça grupları yüklenirken bir hata oluştu.'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <div
                    onClick={selectedCategory && selectedModel && !loadingPartGroups ? () => setIsPartGroupModalOpen(true) : undefined}
                    tabIndex={selectedCategory && selectedModel && !loadingPartGroups ? 0 : -1}
                    className={`form-combobox w-full relative ${
                      selectedCategory && selectedModel && !loadingPartGroups
                        ? 'cursor-pointer bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 hover:border-gray-400 dark:hover:border-gray-500' 
                        : 'cursor-not-allowed opacity-50 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2'
                    } ${partGroupError ? 'border-red-300 dark:border-red-600' : ''}`}
                  >
                    {loadingPartGroups ? (
                      <>
                        <span className="text-gray-500 dark:text-gray-400 truncate pr-1">Yükleniyor...</span>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <svg className="animate-spin h-4 w-4 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      </>
                    ) : (
                      <>
                        <span 
                          className={`${selectedPartGroup ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'} truncate pr-1`}
                        >
                          {selectedPartGroup 
                            ? selectedPartGroup.name 
                            : selectedCategory && selectedModel 
                              ? 'Parça grubu seçin' 
                              : !selectedCategory 
                                ? 'Önce kategori seçin'
                                : 'Önce model seçin'
                          }
                        </span>
                        <svg className={`h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isPartGroupModalOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </>
                    )}
                  </div>
                </div>
              )}
          </div>
          )}
        </div>

        {/* Yedek Parça Listesi */}
          {/* Kategoriler yüklendi veya kategori seçili ise göster */}
          {((!loadingCategories && categories.length > 0) || selectedCategory) && (
            <div className="animate-fadeIn mt-8">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Yedek Parçalar
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                  {isSearchMode ? (
                    <>Arama sonuçları</>
                  ) : selectedCategory ? (
                    selectedPartGroup ? (
                      <><span className="font-medium text-indigo-600 dark:text-indigo-400 inline-block max-w-xs truncate align-bottom">{selectedPartGroup.name}</span> grubundaki yedek parçalar</>
                    ) : selectedModel ? (
                      <><span className="font-medium text-indigo-600 dark:text-indigo-400 inline-block max-w-xs truncate align-bottom">{selectedModel.name}</span> modeline ait yedek parçalar</>
                    ) : (
                      <><span className="font-medium text-indigo-600 dark:text-indigo-400 inline-block max-w-xs truncate align-bottom">{selectedCategory.name}</span> kategorisine ait yedek parçalar</>
                    )
                  ) : (
                    <>🎁 <span className="font-medium text-indigo-600 dark:text-indigo-400">Promosyonlu</span> yedek parçalar</>
                  )}
                </p>
              
              {/* Stokta Olmayanları Gizle Toggle - Arama modunda gizle */}
              {!isSearchMode && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700">
                  <div className="rounded-xl p-4 border border-white dark:border-gray-700 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 rounded-lg flex items-center justify-center mr-3 border border-gray-200 dark:border-gray-600">
                      <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                    </div>
                    <div>
                      <label htmlFor="hide-out-of-stock" className="text-sm font-medium text-gray-900 dark:text-white cursor-pointer">
                        Stok Filtresi
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Stokta olmayan ürünleri gizle</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={hideOutOfStock}
                    onClick={handleHideOutOfStockToggle}
                    className={`${hideOutOfStock 
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700' 
                      : 'bg-gradient-to-br from-gray-300 via-gray-400 to-gray-500 dark:from-gray-600 dark:via-gray-600/90 dark:to-gray-500 hover:from-gray-400 hover:via-gray-500 hover:to-gray-600 dark:hover:from-gray-500 dark:hover:via-gray-500/90 dark:hover:to-gray-400'
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm`}
                  >
                    <span
                      className={`${hideOutOfStock ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out shadow-sm`}
                    />
                  </button>
                  </div>
                </div>
              )}
            </div>

              {loadingSpareParts ? (
                <div className="flex items-center justify-center py-12">
                  <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="ml-3 text-gray-600 dark:text-gray-400">Yedek parçalar yükleniyor...</span>
                </div>
              ) : sparePartsError ? (
                <div className="bg-yellow-50 dark:bg-yellow-900/50 border-l-4 border-yellow-500 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700 dark:text-yellow-200">
                        Yedek parçalar yüklenirken bir hata oluştu.
                      </p>
                    </div>
                  </div>
                </div>
              ) : spareParts.length === 0 && !isSearchMode ? (
                <div className="text-center py-12">
                  {!selectedCategory ? (
                    // Promosyonlu ürünler için özel mesaj
                    <>
                      <div className="flex justify-center mb-6">
                        <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                          </svg>
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                        🎁 Promosyonlu Ürün Bulunamadı
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 max-w-lg mx-auto mb-6 leading-relaxed">
                        Şu anda promosyonlu yedek parça bulunmuyor. Özel fırsatlar için daha sonra tekrar kontrol edebilir veya kategori seçerek tüm ürünleri inceleyebilirsiniz.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <button
                          onClick={() => setIsCategoryModalOpen(true)}
                          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-200"
                        >
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          Kategori Seç
                        </button>
                        <div className="text-center">
                          <p className="text-sm text-gray-500 dark:text-gray-400">veya</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">stok durumunu kontrol edin</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    // Normal kategori seçili ama ürün yok mesajı
                    <>
                      <div className="flex justify-center mb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 rounded-2xl flex items-center justify-center border border-gray-200 dark:border-gray-600">
                          <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                          </svg>
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Yedek Parça Bulunamadı</h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm max-w-md mx-auto">
                        {selectedPartGroup
                          ? `${selectedPartGroup.name} parça grubunda henüz yedek parça bulunmuyor.`
                          : selectedModel
                            ? `${selectedModel.name} modeli için henüz yedek parça bulunmuyor.`
                            : selectedCategory
                              ? `${selectedCategory.name} kategorisinde henüz yedek parça bulunmuyor.`
                              : 'Bu seçim için henüz yedek parça bulunmuyor.'
                        }
                      </p>
                    </>
                  )}
                </div>
              ) : spareParts.length === 0 && isSearchMode ? (
                // Arama modunda hiç sonuç yoksa boş alan göster (toast mesajı zaten gösteriliyor)
                <div className="text-center py-16">
                  <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 rounded-2xl flex items-center justify-center border border-gray-200 dark:border-gray-600">
                      <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Sonuç Bulunamadı</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Arama kriterlerinizi değiştirerek tekrar deneyin</p>
                </div>
              ) : (
                <>
                  {/* Yedek Parça Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                    {spareParts.map((sparePart) => (
                      <SparePartCard
                        key={sparePart.Id}
                        sparePart={sparePart}
                        onAddToCart={handleAddToCart}
                      />
                    ))}
                  </div>

                  {/* Sayfalama */}
                  {totalPages > 1 && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-sm mt-6">
                      <div className="rounded-xl p-4 sm:p-5 border border-white dark:border-gray-700">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center justify-center sm:justify-start">
                          <div className="w-8 h-8 bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 dark:from-gray-700 dark:via-gray-700/80 dark:to-gray-600 rounded-lg flex items-center justify-center mr-3 border border-indigo-100 dark:border-gray-600">
                            <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="text-center sm:text-left">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              Sayfa {currentPage} / {totalPages}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Toplam {totalSpareParts} ürün
                            </p>
                          </div>
                        </div>
                        <div className="flex justify-center sm:justify-end space-x-2">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="border border-gray-300 dark:border-gray-600 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                          >
                            <div className="rounded-lg px-3 sm:px-4 py-2 border border-white dark:border-gray-700 flex items-center text-sm font-medium text-gray-700 dark:text-gray-200">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                              </svg>
                              <span className="hidden sm:inline">Önceki</span>
                            </div>
                          </button>
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="border border-gray-300 dark:border-gray-600 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                          >
                            <div className="rounded-lg px-3 sm:px-4 py-2 border border-white dark:border-gray-700 flex items-center text-sm font-medium text-gray-700 dark:text-gray-200">
                              <span className="hidden sm:inline">Sonraki</span>
                              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </button>
                        </div>
                      </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
      </div>
    );
  };



  return (
    <PageTransition>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 relative">
          <h1 className={`text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>Yedek Parça Siparişi</h1>
          
          {/* Floating Geri Butonu - Sadece mobilde görünür */}
          <AnimatePresence>
            {!loadingCart && !isInitialLoad && showBackButton && !(isMobile && isSidebarOpen) && (
              <motion.div
                className="fixed top-4 right-4 z-50 md:hidden"
                initial={{ scale: 0, opacity: 0, x: 20, y: -20 }}
                animate={{ scale: 1, opacity: 1, x: 0, y: 0 }}
                exit={{ scale: 0, opacity: 0, x: 20, y: -20 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 25,
                  duration: 0.3
                }}
              >
                <motion.button
                  onClick={handleGoBack}
                  className="relative border border-gray-200 dark:border-gray-700 rounded-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 hover:from-gray-100 hover:to-gray-200 dark:hover:from-gray-700 dark:hover:to-gray-600 text-gray-700 dark:text-gray-300 shadow-lg shadow-gray-500/25 dark:shadow-black/25 hover:shadow-xl hover:shadow-gray-500/30 dark:hover:shadow-black/35 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 dark:focus:ring-gray-500 dark:focus:ring-offset-gray-800"
                  aria-label="Geri Git"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <div className="rounded-full p-2.5 border border-white dark:border-gray-700">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </div>
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Sepet Butonu - Mobilde geri butonunun altında, desktop'ta en üstte */}
          <AnimatePresence>
            {!loadingCart && !isInitialLoad && showFixedButtons && !(isMobile && isSidebarOpen) && (
              <motion.div
                className="fixed top-20 md:top-4 right-4 z-50"
                initial={{ scale: 0, opacity: 0, x: 20, y: -20 }}
                animate={{ scale: 1, opacity: 1, x: 0, y: 0 }}
                exit={{ scale: 0, opacity: 0, x: 20, y: -20 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 25,
                  delay: 0.1
                }}
              >
                <motion.button
                  onClick={() => {
                    navigate('/cart');
                  }}
                  className="relative bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white p-2.5 rounded-full shadow-lg shadow-slate-500/25 hover:shadow-xl hover:shadow-slate-500/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
                  aria-label="Sepet"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l-1 12H6L5 9z" />
              </svg>
              
                              {/* Sepet Sayısı Badge */}
                <AnimatePresence>
                  {cartItemCount > 0 && (
                    <motion.span
                      className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px] px-1"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ type: "spring", stiffness: 500, damping: 25 }}
                      key={cartItemCount}
                    >
                      <motion.span
                        key={cartItemCount}
                        initial={{ scale: 1.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        {cartItemCount > 99 ? '99+' : cartItemCount}
                      </motion.span>
                    </motion.span>
                  )}
                </AnimatePresence>
              

              {loadingCart && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-600 rounded-full">
                  <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Arama Butonu - Sepet butonunun altında */}
          <AnimatePresence>
            {!loadingCart && !isInitialLoad && showSearchButton && !(isMobile && isSidebarOpen) && (
              <motion.div
                className="fixed top-32 md:top-16 right-4 z-50"
                initial={{ scale: 0, opacity: 0, x: 20, y: -20 }}
                animate={{ scale: 1, opacity: 1, x: 0, y: 0 }}
                exit={{ scale: 0, opacity: 0, x: 20, y: -20 }}
                transition={{
                  type: "spring", 
                  stiffness: 400,
                  damping: 25,
                  delay: 0.2
                }}
              >
                <motion.button
                  onClick={() => setIsSearchModalOpen(true)}
                  className="relative bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white p-2.5 rounded-full shadow-lg shadow-slate-500/25 hover:shadow-xl hover:shadow-slate-500/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
                  aria-label="Arama"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Main Content Card */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg overflow-hidden">
          <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
            {renderMainContent()}
          </div>
        </div>

        {/* Toast */}
        <Toast
          isVisible={toast.isVisible}
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />

        {/* Kategori seçimi modal'ı */}
        <CategorySelector
          isOpen={isCategoryModalOpen}
          onClose={() => setIsCategoryModalOpen(false)}
          onSelect={handleCategorySelect}
          categories={categories}
          searchTerm={categorySearchTerm}
          onSearchChange={handleCategorySearchChange}
          selectedCategoryId={selectedCategory?.guid}
        />

        {/* Model seçimi modal'ı */}
        <ModelSelector
          isOpen={isModelModalOpen}
          onClose={() => setIsModelModalOpen(false)}
          onSelect={handleModelSelect}
          models={models}
          searchTerm={modelSearchTerm}
          onSearchChange={handleModelSearchChange}
          selectedModelId={selectedModel?.guid}
        />

        {/* Parça grubu seçimi modal'ı */}
        <SparePartsGroupSelector
          isOpen={isPartGroupModalOpen}
          onClose={() => setIsPartGroupModalOpen(false)}
          onSelect={handlePartGroupSelect}
          groups={partGroups}
          searchTerm={partGroupSearchTerm}
          onSearchChange={handlePartGroupSearchChange}
          selectedGroupId={selectedPartGroup?.guid}
        />

        {/* Arama modal'ı */}
        <PartSearchModal
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          onSearch={handleSearch}
          searchLoading={searchLoading}
        />
      </div>
    </PageTransition>
  );
} 